import { SetStateAction, useCallback, useMemo, useState } from "react";
import { useUser } from "@/hooks/use-user";
import { usePaymentConfig, getPaymentMethodBlockedReason } from "@/hooks/use-payment-config";
import { chargePayment, isCardUpdateRequiredError } from "@/utils/charge";
import { useLocation } from "wouter";
import { getSignedTransactionData } from "@/utils/transactions";
import { Loader2 } from "lucide-react";
import { usePosCartGuard } from "@/providers/PosCartGuardProvider";

import type { MenuTypesWithProduct, SechUser } from "@/types/pos";

import LocationSelection from "@/components/pos/location-selection";
import MainMenu from "@/components/pos/main-menu";
import OrderConfirmation from "@/components/pos/order-confirmation";

import { PosCartProvider } from "@/hooks/pos/use-cart";
import { PosOrderProvider } from "@/hooks/pos/use-order";

export default function OrderPage() {
	const { setPosHasItems } = usePosCartGuard();
	const { config: paymentConfig } = usePaymentConfig();
	const [, navigate] = useLocation();

	const {
		uid,
		walletAddress,
		isConnected,
		balance,
		email,
		displayName,
		updateBalance,
		loading,
	} = useUser();

	const [selectedLocation, setSelectedLocation] = useState<{
		id: number;
		name?: string;
		details?: string;
		menuTypes?: MenuTypesWithProduct[];
		isLoading?: boolean;
	} | null>(null);
	const [showMainMenu, setShowMainMenu] = useState(false);
	const [showOrderConfirmation, setShowOrderConfirmation] = useState(false);
	const [confirmedOrder, setConfirmedOrder] = useState(null);

	// Map main app user data to SechUser shape for POS hooks
	const sechUser: SechUser | null = useMemo(() => {
		if (!uid) return null;
		return {
			uid,
			walletAddress: walletAddress || "",
			balance: typeof balance === "number" ? balance : 0,
			email: email || "",
			displayName: displayName || "",
		};
	}, [uid, walletAddress, balance, email, displayName]);

	const handleLocationSelect = useCallback(
		(location: {
			id: number;
			name?: string;
			details?: string;
			menuTypes?: MenuTypesWithProduct[];
			isLoading?: boolean;
		}) => {
			setSelectedLocation(location);
			setShowMainMenu(true);
		},
		[]
	);

	const handlePaymentSuccess = useCallback(
		(order: SetStateAction<null>) => {
			setConfirmedOrder(order);
			setShowOrderConfirmation(true);
			setPosHasItems(false);
		},
		[setPosHasItems]
	);

	// Payment execution - called by PosCartProvider when customer places an order
	const executePayment = useCallback(
		async (paymentData: any): Promise<{ success: boolean; txHash?: string; orderId?: string; error?: string }> => {
			try {
				const { orderId, total, subtotal, appliedDiscount, derivedDiscountUsd, tax_amount, taxBreakdown, items, location, paymentMethodType, paymentMethodId } = paymentData;

				// Redundancy check: confirm the selected payment method is still accepted
				const blocked = getPaymentMethodBlockedReason(
					(paymentMethodType === 'CARD' ? 'CARD' : 'TOKEN'),
					paymentConfig,
				);
				if (blocked) {
					return { success: false, error: blocked };
				}

				const amount = typeof total.sech === "number" ? total.sech.toFixed(5) : total.sech;

				let signature: string | undefined;
				let nonce: number | undefined;
				let forwardRequest: any;

				// TOKEN flow: get wallet signature
				if (paymentMethodType !== 'CARD' && isConnected) {
					const {
						signedTx,
						txNonce,
						transferRequest: signatureData,
					} = await getSignedTransactionData({
						userAddress: walletAddress,
						amount,
					});
					signature = signedTx;
					nonce = txNonce;
					forwardRequest = signatureData;
				}

				// Fresh per-click idempotency key so retries / Mobius see distinct attempts
				const idempotencyKey = typeof crypto !== 'undefined' && 'randomUUID' in crypto
					? crypto.randomUUID()
					: `${Date.now()}-${Math.random().toString(36).slice(2)}`;

				// Resolve a discountAmount that's internally consistent with the
				// subtotal / tax_amount / total we're about to send. The BE check is
				// `total === subtotal - discountAmount + tax_amount` (with non-negative
				// tax_amount), so we work backwards from the totals themselves rather
				// than trusting any single state source. Three things can produce a
				// shortfall between subtotal+tax and total:
				//   (a) appliedDiscount state (promo / auto-apply that flowed through
				//       getDiscountedTotal),
				//   (b) the cart-derived derivedDiscountUsd (same idea, recomputed),
				//   (c) a payment-method rebate the BE returned inside
				//       /v1/taxes/calculate as a NEGATIVE fee — the FE summed it into
				//       tax_amount, leaving total < subtotal with no appliedDiscount.
				// Coerce any negative net tax into discountAmount and floor tax_amount
				// at 0, then take the max of all three sources so we never under-declare.
				const rawTaxUsd = typeof tax_amount?.usd === 'number' ? tax_amount.usd : 0;
				const taxAmountForBe = Math.max(0, rawTaxUsd);
				const negativeTaxAsDiscount = Math.max(0, -rawTaxUsd);
				const derivedFromTotals = Math.max(0, subtotal.usd + taxAmountForBe - total.usd);
				const fromState = typeof derivedDiscountUsd === 'number'
					? derivedDiscountUsd
					: (appliedDiscount ? appliedDiscount.calculatedAmount : 0);
				const effectiveDiscountUsd = Math.max(fromState, derivedFromTotals, negativeTaxAsDiscount);

				// Process payment through unified charge endpoint
				// Backend expects total/subtotal/tax_amount as plain USD numbers.
				// `subtotal` is the PRE-discount subtotal — backend re-applies the discount.
				const json = await chargePayment({
					transactionType: 'order',
					paymentMethodType: paymentMethodType || 'TOKEN',
					amount: total.usd,
					...(paymentMethodType === 'CARD' && { paymentMethodId }),
					walletAddress,
					...(paymentMethodType !== 'CARD' && { signature, forwardRequest, nonce }),
					idempotencyKey,
					// Always send discountAmount when total is below subtotal+tax, even if
					// appliedDiscount was nulled or the discount lives inside a negative
					// tax fee. Otherwise BE rejects with "total mismatch / total may be
					// below subtotal only when discountAmount accounts for the difference".
					...(effectiveDiscountUsd > 0 && {
						discountAmount: effectiveDiscountUsd,
						...(appliedDiscount && {
							discountId: appliedDiscount.discountId,
							discountCode: appliedDiscount.promoCode,
						}),
					}),
					context: {
						orderId,
						total: total.usd,
						subtotal: subtotal.usd,
						tax_amount: taxAmountForBe,
						taxBreakdown,
						items,
						location,
					},
				});

				await updateBalance();

				return {
					...json,
					orderId,
				};
			} catch (err: any) {
				// Invalid card vault: send the customer to re-enter their card instead
				// of leaving them able to retry the same (now-flagged) card.
				if (isCardUpdateRequiredError(err)) {
					navigate('/account/payment-methods');
					return {
						success: false,
						error: 'Please re-enter your card details to continue.',
					};
				}
				return {
					success: false,
					error: err.message,
				};
			}
		},
		[isConnected, walletAddress, updateBalance, paymentConfig, navigate]
	);

	// Show loading while user data is loading
	if (loading) {
		return (
			<div className="min-h-screen bg-[#18181B] flex items-center justify-center">
				<Loader2 className="h-6 w-6 animate-spin text-gray-400" />
			</div>
		);
	}

	// Show order confirmation
	if (showOrderConfirmation && confirmedOrder) {
		return (
			<div className="bg-[#18181B] h-screen relative overflow-x-hidden overflow-y-auto">
				<OrderConfirmation
					order={confirmedOrder}
					onBackToMenu={() => {
						setShowOrderConfirmation(false);
						setConfirmedOrder(null);
						setShowMainMenu(false);
					}}
				/>
			</div>
		);
	}

	return (
		<div className="bg-[#18181B] h-screen relative overflow-x-hidden overflow-y-auto">
			<PosOrderProvider userId={sechUser?.uid!}>
				<PosCartProvider
					sechUser={sechUser}
					executePayment={executePayment}
					onPaymentSuccess={handlePaymentSuccess}
					onCartChange={setPosHasItems}
					location={selectedLocation}
				>
					{!showMainMenu && (
						<LocationSelection onLocationSelect={handleLocationSelect} />
					)}

					{showMainMenu && selectedLocation && (
						<MainMenu
							location={selectedLocation}
							onBackToLocation={() => setShowMainMenu(false)}
						/>
					)}
				</PosCartProvider>
			</PosOrderProvider>
		</div>
	);
}
